import { Button } from "primereact/button";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";

import {
  POINT_RULE_ACTION_LABELS,
  POINT_RULE_KIND_LABELS,
  PointRule,
  PointRuleActionType,
  PointRuleKind,
  isPointRuleActionRetired,
} from "../../../models/pointRule";

export interface PointRuleFilterValues {
  title: string;
  actionType: PointRuleActionType | null;
  ruleKind: PointRuleKind | null;
  active: boolean | null;
}

export const EMPTY_POINT_RULE_FILTERS: PointRuleFilterValues = {
  title: "",
  actionType: null,
  ruleKind: null,
  active: null,
};

export const hasPointRuleFilters = (filters: PointRuleFilterValues): boolean =>
  filters.title.trim() !== "" ||
  filters.actionType !== null ||
  filters.ruleKind !== null ||
  filters.active !== null;

const normalizeForSearch = (value: string): string =>
  value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

/** Filtro en el cliente: la lista de reglas de un tenant ya viene completa del backend. */
export const filterPointRules = (rules: PointRule[], filters: PointRuleFilterValues): PointRule[] => {
  const search = normalizeForSearch(filters.title);
  return rules.filter((rule) => {
    // Las reglas de acciones retiradas no se listan, aunque sigan en la base.
    if (isPointRuleActionRetired(rule.actionType)) return false;
    if (filters.actionType !== null && rule.actionType !== filters.actionType) return false;
    if (filters.ruleKind !== null && rule.ruleKind !== filters.ruleKind) return false;
    if (filters.active !== null && rule.active !== filters.active) return false;
    if (search === "") return true;
    const haystack = normalizeForSearch(
      `${rule.displayTitle ?? ""} ${rule.displayDescription ?? ""} ${POINT_RULE_ACTION_LABELS[rule.actionType]}`
    );
    return haystack.includes(search);
  });
};

const enumOptions = <T extends string>(labels: Record<T, string>) =>
  (Object.keys(labels) as T[]).map((value) => ({ label: labels[value], value }));

const ACTION_OPTIONS = enumOptions<PointRuleActionType>(POINT_RULE_ACTION_LABELS).filter(
  (option) => !isPointRuleActionRetired(option.value)
);
const KIND_OPTIONS = enumOptions<PointRuleKind>(POINT_RULE_KIND_LABELS);
const STATUS_OPTIONS = [
  { label: "Activa", value: true },
  { label: "Inactiva", value: false },
];

interface PointRuleFiltersProps {
  filters: PointRuleFilterValues;
  onChange: (filters: PointRuleFilterValues) => void;
}

/** Mismo patrón que BenefitFilters: desplegables con showClear y un único "Limpiar filtros". */
export const PointRuleFilters = ({ filters, onChange }: PointRuleFiltersProps) => {
  const setFilter = <K extends keyof PointRuleFilterValues>(key: K, value: PointRuleFilterValues[K]) =>
    onChange({ ...filters, [key]: value });

  return (
    <div className="grid mt-3 mb-2 row-gap-3">
      <div className="col-12 md:col-3">
        <FloatLabel className="w-full">
          <InputText
            id="pointRuleTitleFilter"
            name="pointRuleTitleFilter"
            className="w-full"
            value={filters.title}
            onChange={(event) => setFilter("title", event.currentTarget.value)}
          />
          <label htmlFor="pointRuleTitleFilter">Título</label>
        </FloatLabel>
      </div>
      <div className="col-12 md:col-3">
        <FloatLabel className="w-full">
          <Dropdown
            inputId="pointRuleActionFilter"
            className="w-full"
            value={filters.actionType}
            options={ACTION_OPTIONS}
            optionLabel="label"
            optionValue="value"
            onChange={(event: DropdownChangeEvent) => setFilter("actionType", event.value ?? null)}
            showClear
          />
          <label htmlFor="pointRuleActionFilter">Acción</label>
        </FloatLabel>
      </div>
      <div className="col-12 md:col-3">
        <FloatLabel className="w-full">
          <Dropdown
            inputId="pointRuleKindFilter"
            className="w-full"
            value={filters.ruleKind}
            options={KIND_OPTIONS}
            optionLabel="label"
            optionValue="value"
            onChange={(event: DropdownChangeEvent) => setFilter("ruleKind", event.value ?? null)}
            showClear
          />
          <label htmlFor="pointRuleKindFilter">Tipo de regla</label>
        </FloatLabel>
      </div>
      <div className="col-12 md:col-3">
        <FloatLabel className="w-full">
          <Dropdown
            inputId="pointRuleStatusFilter"
            className="w-full"
            value={filters.active}
            options={STATUS_OPTIONS}
            optionLabel="label"
            optionValue="value"
            onChange={(event: DropdownChangeEvent) => setFilter("active", event.value ?? null)}
            showClear
          />
          <label htmlFor="pointRuleStatusFilter">Estado</label>
        </FloatLabel>
      </div>
      <div className="col-12 flex justify-content-end">
        <Button
          type="button"
          label="Limpiar filtros"
          icon="pi pi-filter-slash"
          severity="secondary"
          outlined
          size="small"
          disabled={!hasPointRuleFilters(filters)}
          onClick={() => onChange(EMPTY_POINT_RULE_FILTERS)}
        />
      </div>
    </div>
  );
};
